import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { useAuth } from '../contexts/AuthContext';
import { Check, Star, Users, Building, Zap, ArrowRight, Home, Rocket, Crown, Sparkles } from 'lucide-react';
import ImprovedUniversalHeader from '../components/ImprovedUniversalHeader';
import JsonLd from '../components/SEO/JsonLd';

const Pricing = () => {
  const { user, profile } = useAuth();
  const navigate = useNavigate();

  const urlParams = new URLSearchParams(window.location.search);
  const planType = urlParams.get('type') || 'all';
  const userType = profile?.user_type;

  const renterPlans = [
    {
      name: 'Just Browsing',
      price: 'Free',
      period: '',
      description: 'Perfect for casual browsing and exploration',
      features: [
        '10 swipes per day',
        'Basic property browsing',
        'Limited AI matching with Fiona',
        'Basic messaging with landlords',
        'Property comparison (up to 3)',
        'Neighborhood insights',
        'Mobile app access',
        'Community support'
      ],
      cta: 'Current Plan',
      popular: false,
      userType: 'renter',
      tier: 'free'
    },
    {
      name: 'All In',
      price: '$4.99',
      period: '/month',
      description: 'Unlimited access to all renter features',
      features: [
        'Unlimited daily swipes',
        'Advanced AI-powered matching',
        'Priority messaging with landlords',
        'Unlimited property comparisons',
        'Advanced neighborhood insights',
        'Priority customer support',
        'Early access to new features',
        'Saved searches & alerts',
        'Advanced filtering options',
        'Tour scheduling priority'
      ],
      cta: 'Upgrade to All In',
      popular: true,
      userType: 'renter',
      tier: 'premium'
    }
  ];

  const landlordPlans = [
    {
      name: 'Starter',
      price: '$12',
      period: '/unit/month',
      unitRange: '1-3 units',
      description: 'Perfect for getting started with 1-3 properties',
      features: [
        'List up to 3 properties',
        'AI-powered tenant matching',
        'Secure messaging with renters',
        'Tour scheduling',
        'Basic analytics',
        'Application management',
        'Email support'
      ],
      cta: 'Get Started Free',
      popular: false,
      userType: 'landlord',
      tier: 'starter'
    },
    {
      name: 'Growth',
      price: '$9',
      period: '/unit/month',
      unitRange: '4-15 units',
      description: 'Scale your portfolio with advanced tools',
      features: [
        'List up to 15 properties',
        'Everything in Starter',
        'Advanced tenant screening',
        'Automated lease management',
        'Financial reporting',
        'Priority customer support',
        'Background check integration'
      ],
      cta: 'Start Growing',
      popular: true,
      userType: 'landlord',
      tier: 'growth'
    },
    {
      name: 'Pro',
      price: '$6',
      period: '/unit/month',
      unitRange: '16-50 units',
      description: 'Professional tools for property managers',
      features: [
        'List up to 50 properties',
        'Everything in Growth',
        'Advanced analytics dashboard',
        'Multi-property reporting',
        'Team member access',
        'API access',
        'Dedicated support'
      ],
      cta: 'Go Pro',
      popular: false,
      userType: 'landlord',
      tier: 'pro'
    },
    {
      name: 'Enterprise',
      price: '$4-5',
      period: '/unit/month',
      unitRange: '50+ units',
      description: 'Custom solutions for large portfolios',
      features: [
        'Unlimited property listings',
        'Everything in Pro',
        'Dedicated account manager',
        'Custom integrations',
        'White-label options',
        'SLA guarantees',
        'Custom reporting',
        'Training & onboarding'
      ],
      cta: 'Contact Sales',
      popular: false,
      userType: 'landlord',
      tier: 'enterprise'
    }
  ];

  let plansToShow = [];
  if (planType === 'renter' || userType === 'renter') {
    plansToShow = renterPlans;
  } else if (planType === 'landlord' || userType === 'landlord') {
    plansToShow = landlordPlans;
  } else {
    plansToShow = [...renterPlans, ...landlordPlans];
  }

  const handleGetStarted = (plan: any) => {
    if (user) {
      if (plan.userType === 'landlord') {
        navigate('/landlord-dashboard');
      } else if (plan.tier === 'premium') {
        navigate('/dashboard');
      } else {
        navigate('/discover');
      }
    } else {
      navigate('/signup');
    }
  };

  const getPageTitle = () => {
    if (planType === 'renter' || userType === 'renter') {
      return 'Renter Plans';
    } else if (planType === 'landlord' || userType === 'landlord') {
      return 'Landlord Plans';
    }
    return 'Choose Your Plan';
  };

  const getPlanIcon = (tier: string) => {
    switch (tier) {
      case 'starter': return <Home className="w-6 h-6 text-primary" />;
      case 'growth': return <Rocket className="w-6 h-6 text-primary" />;
      case 'pro': return <Crown className="w-6 h-6 text-primary" />;
      case 'enterprise': return <Building className="w-6 h-6 text-primary" />;
      case 'premium': return <Sparkles className="w-6 h-6 text-primary" />;
      case 'free': return <Star className="w-6 h-6 text-primary" />;
      default: return null;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950">
      <ImprovedUniversalHeader 
        title={getPageTitle()}
        showBackButton={true}
      />
      
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-20 left-20 w-72 h-72 bg-primary/10 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-20 right-20 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '3s' }}></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-80 h-80 bg-purple-500/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1.5s' }}></div>
      </div>

      <div className="relative z-10 py-16 md:py-24">
        <JsonLd data={{
          '@context': 'https://schema.org',
          '@type': 'OfferCatalog',
          name: 'Flamingo Pricing',
          url: `${window.location.origin}/pricing`,
          itemListElement: [
            ...renterPlans.map((p) => ({
              '@type': 'Offer',
              name: p.name,
              price: p.price.replace('$',''),
              priceCurrency: 'USD',
              category: 'Renter',
            })),
            ...landlordPlans.map((p) => ({
              '@type': 'Offer',
              name: p.name,
              price: p.price.replace('$','').replace('-5',''),
              priceCurrency: 'USD',
              category: 'Landlord',
            }))
          ]
        }} />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h1 className="font-heading text-4xl md:text-5xl font-bold text-white mb-6">
              {getPageTitle()}
            </h1>
            <p className="text-xl text-slate-300 max-w-3xl mx-auto leading-relaxed">
              {planType === 'renter' || userType === 'renter' 
                ? 'Find your perfect home with the right plan for your needs'
                : planType === 'landlord' || userType === 'landlord'
                ? 'Start free and scale as you grow. Pay only for what you use.'
                : 'Choose the perfect plan for your rental journey'
              }
            </p>
            {(planType === 'landlord' || userType === 'landlord') && (
              <div className="mt-6 inline-flex items-center gap-2 bg-green-500/20 border border-green-500/30 text-green-400 px-4 py-2 rounded-full text-sm font-medium">
                <Sparkles className="w-4 h-4" />
                Free to get started • No credit card required
              </div>
            )}
          </div>

          {planType === 'all' && !userType && (
            <div className="flex justify-center mb-12">
              <div className="bg-slate-800/70 backdrop-blur-xl border border-slate-700/50 rounded-2xl p-2 inline-flex">
                <button
                  onClick={() => navigate('/pricing?type=renter')}
                  className="px-6 py-3 rounded-xl text-slate-300 hover:text-white hover:bg-slate-700/50 transition-all duration-300"
                >
                  I'm a Renter
                </button>
                <button
                  onClick={() => navigate('/pricing?type=landlord')}
                  className="px-6 py-3 rounded-xl text-slate-300 hover:text-white hover:bg-slate-700/50 transition-all duration-300"
                >
                  I'm a Landlord
                </button>
              </div>
            </div>
          )}

          <div className={`grid gap-6 max-w-6xl mx-auto ${
            plansToShow.length === 2 ? 'md:grid-cols-2' : 
            plansToShow.length === 4 ? 'md:grid-cols-2 lg:grid-cols-4' : 
            'md:grid-cols-2 lg:grid-cols-3'
          }`}>
            {plansToShow.map((plan, index) => (
              <div key={index} className={`relative bg-slate-800/70 backdrop-blur-xl border border-slate-700/50 rounded-3xl p-6 transition-all duration-300 hover:scale-105 hover:bg-slate-800/80 ${plan.popular ? 'ring-2 ring-primary shadow-2xl scale-105 shadow-primary/25' : 'hover:shadow-2xl'}`}>
                {plan.popular && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <div className="bg-gradient-to-r from-primary to-blue-500 text-white px-6 py-2 rounded-full text-sm font-semibold shadow-lg flex items-center space-x-2">
                      <Crown className="w-4 h-4" />
                      <span>Most Popular</span>
                    </div>
                  </div>
                )}

                <div className="text-center mb-6">
                  <h3 className="font-heading text-xl font-bold text-white mb-2 flex items-center justify-center space-x-2">
                    {getPlanIcon(plan.tier)}
                    <span>{plan.name}</span>
                  </h3>
                  {'unitRange' in plan && (
                    <p className="text-primary font-medium text-sm mb-2">{plan.unitRange}</p>
                  )}
                  <p className="text-slate-400 text-sm mb-4">{plan.description}</p>
                  
                  <div className="mb-4">
                    <span className="text-3xl font-bold text-white">{plan.price}</span>
                    {plan.period && <span className="text-slate-400 text-sm">{plan.period}</span>}
                  </div>
                </div>

                <ul className="space-y-3 mb-6">
                  {plan.features.map((feature, featureIndex) => (
                    <li key={featureIndex} className="flex items-start space-x-2">
                      <Check className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" />
                      <span className="text-slate-300 text-sm">{feature}</span>
                    </li>
                  ))}
                </ul>

                <Button 
                  onClick={() => handleGetStarted(plan)} 
                  className={`w-full py-3 text-sm font-semibold rounded-xl transition-all duration-300 ${
                    plan.popular 
                      ? 'bg-gradient-to-r from-primary to-blue-500 hover:from-primary/90 hover:to-blue-500/90 text-white shadow-xl hover:shadow-primary/25' 
                      : 'bg-slate-700/50 hover:bg-slate-600/50 text-white border border-slate-600/50 hover:border-slate-500/50'
                  }`}
                >
                  {plan.cta}
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            ))}
          </div>

          <div className="mt-24">
            <div className="text-center mb-12">
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-white mb-4">
                Why Choose Flamingo?
              </h2>
              <p className="text-xl text-slate-300">
                Built for the modern rental experience
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              <div className="text-center group">
                <div className="w-16 h-16 bg-gradient-to-br from-primary/20 to-primary/40 rounded-3xl flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-all duration-300 border border-primary/30">
                  <Zap className="w-8 h-8 text-primary" />
                </div>
                <h3 className="font-heading text-xl font-bold mb-4 text-white">Lightning Fast</h3>
                <p className="text-slate-400">Find matches in seconds with our AI-powered algorithm</p>
              </div>

              <div className="text-center group">
                <div className="w-16 h-16 bg-gradient-to-br from-blue-500/20 to-blue-500/40 rounded-3xl flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-all duration-300 border border-blue-500/30">
                  <Users className="w-8 h-8 text-blue-400" />
                </div>
                <h3 className="font-heading text-xl font-bold mb-4 text-white">Trusted Community</h3>
                <p className="text-slate-400">Join verified renters and landlords</p>
              </div>

              <div className="text-center group">
                <div className="w-16 h-16 bg-gradient-to-br from-green-500/20 to-green-500/40 rounded-3xl flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-all duration-300 border border-green-500/30">
                  <Building className="w-8 h-8 text-green-400" />
                </div>
                <h3 className="font-heading text-xl font-bold mb-4 text-white">Complete Solution</h3>
                <p className="text-slate-400">From search to lease signing, we've got you covered</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Pricing;
